import React, { useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Link, NavLink, useLocation } from "react-router-dom";

/** Actions */
import { logout } from "../app/slice/authSlice";

/** Contexts */
import { useMenu } from "../context/MenuContext";

/** Config Files */
import { generalMenuItems, adminMenuItems } from "./sidebar.config";

/** Utilities */
import { filterMenu } from "../utils/common/sidebarHelper";

/** Styles */
import "./Sidebar.scss";

const Sidebar = ({ isClosed, openMenus, toggleMenu }) => {

  const dispatch = useDispatch();
  const location = useLocation();

  const { showAdminMenu, toggleAdminMenu } = useMenu();

  const menuToUse = showAdminMenu ? adminMenuItems : generalMenuItems;
  const sidebarMenuToLoad = useMemo(() => filterMenu(menuToUse), [menuToUse]);

  const getReportPath = (title) => {
    return `/${title.replace(/\s+/g, "-").toLowerCase()}`;
  };

  const { pathname } = location;

  const [isHovered, setIsHovered] = useState(false);
  const enableMenuClick = !isClosed || isHovered;

  const handleLogout = () => {
    dispatch(logout());
  };

  const isSubMenuActive = (subMenu, pathname, getReportPath) => {
    return subMenu.some((sub) => {
      const path = sub.isReport ? getReportPath(sub.title) : sub.path;
      return pathname === path;
    });
  };

  const renderProfileItem = (item) => (
    <li key={item.id}>
      <div className="profile-details">
        <div className="profile-content">
          <img src={item.image} alt="profileImg" />
        </div>
        <div className="name-job">
          <div className="profile_name">{item.name}</div>
          <div className="job">{item.job}</div>
        </div>
        <i className={item.icon}></i>
      </div>
    </li>
  );

  const renderExpandableMenuItem = (item) => (
    <div
      className="icon-btn iocn-link"
      role="button"
      tabIndex={0}
      onClick={enableMenuClick ? () => toggleMenu(item.id) : undefined}
    >
      <div className="menu-parent-content">
        <i className={item.icon}></i>
        <span className="link_name">{item.title}</span>
      </div>
      <i className="pi pi-chevron-down arrow-icon"></i>
    </div>
  );

  const renderSimpleMenuItem = (item) => (
    <div className="icon-btn">
      <NavLink
        to={item.isReport ? getReportPath(item.title) : item.path}
        end
        className={({ isActive }) => (isActive ? "active-link" : "")}
      >
        <i className={item.icon}></i>
        <span className="link_name">{item.title}</span>
      </NavLink>
    </div>
  );

  const renderSubMenu = (item) => {
    if (!item.subMenu) {
      return (
        <ul className="sub-menu blank">
          <li>
            <NavLink
              to={item.isReport ? getReportPath(item.title) : item.path}
              className="link_name"
            >
              {item.title}
            </NavLink>
          </li>
        </ul>
      );
    }

    return (
      <ul className={`sub-menu ${item.subMenu[0]?.blank ? "blank" : ""}`}>
        <li>
          <span className="link_name">{item.title}</span>
        </li>
        {item.subMenu.map((sub) => (
          <li key={sub.id || sub.path}>
            <NavLink
              to={sub.isReport ? getReportPath(sub.title) : sub.path}
              end
              className={({ isActive }) => (isActive ? "active-sublink" : "")}
            >
              {sub.title}
            </NavLink>
          </li>
        ))}
      </ul>
    );
  };

  const renderMenuItem = (item) => {
    const isExpandable = item.subMenu && !item.subMenu[0]?.blank;
    const isActive = isParentActive(item) || isItemActive(item);
    const className = `main-menu ${isExpandable && openMenus[item.id] ? "showMenu" : ""} ${isActive ? "active-menu" : ""}`;

    return (
      <li key={item.id} className={className}>
        {isExpandable ? renderExpandableMenuItem(item) : renderSimpleMenuItem(item)}
        {renderSubMenu(item)}
      </li>
    );
  };




  /**
   * Effect to automatically open the parent menu if a sub-menu item is active
   */
  useEffect(() => {
    let activeParentId = null;

    sidebarMenuToLoad.forEach((section) => {
      section.items.forEach((item) => {
        if (!item.subMenu || item.subMenu[0]?.blank) return;

        const isActive = isSubMenuActive(
          item.subMenu,
          pathname,
          getReportPath
        );

        if (isActive) {
          activeParentId = item.id;
        }
      });
    });

    if (activeParentId) {
      toggleMenu(activeParentId, true);
    }
  }, [pathname, sidebarMenuToLoad]);

  const isParentActive = (item) => {
    if (!item.subMenu || item.subMenu[0]?.blank) return false;

    return item.subMenu.some((sub) => {
      const path = sub.isReport ? getReportPath(sub.title) : sub.path;
      return pathname === path;
    });
  };

  const isItemActive = (item) => {
    if (item.subMenu && !item.subMenu[0]?.blank) return false;

    const path = item.isReport ? getReportPath(item.title) : item.path;
    return pathname === path;
  };

  return (
    <div className="sidebar-menu-list">
      <div 
        className={`sidebar ${isClosed ? "close" : ""} ${isClosed && isHovered ? "is-hovered" : ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div>
        <Link
          className="logo-details cursor-pointer"
          to="/"
          onClick={() => {
            toggleAdminMenu(false);
            toggleMenu();
          }}
        >
          <span className="logos">
            <img className="big-logo" src="/images/logoW.png" alt="logo" />
            <img className="small-logo" src="/images/icon.png" alt="icon" />
          </span>
        </Link>
        </div>

        <ul className="nav-links">
          {sidebarMenuToLoad.map((section) => (
            <React.Fragment key={section.id || section.section}>
              <li className="menu-title">{section.section}</li>

              {section.items.map((item) =>
                item.profile
                  ? renderProfileItem(item)
                  : renderMenuItem(item)
              )}
            </React.Fragment>
          ))}
        </ul>

        <div className="sidebar-footer">
          <button
            type="button"
            className="logout-btn"
            onClick={handleLogout}
            aria-label="Logout"
            title="Logout"
          >
            <i className="pi pi-sign-out"></i>
            <span className="link_name">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  isClosed: PropTypes.bool,
  openMenus: PropTypes.objectOf(PropTypes.bool),
  toggleMenu: PropTypes.func.isRequired,
};

export default Sidebar;
